/**
 * Cloud Functions Wrappers for Email Accounts
 *
 * Client-side wrappers for calling Firebase Cloud Functions related to OAuth2 email accounts.
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "@/base";
import type { OAuth2AuthorizationResponse, EmailAccountFormData } from "./schema/email-account.types";

/**
 * Request body for starting OAuth2 authorization
 */
export interface StartOAuth2AuthorizationRequest {
  account_id: string;
  success_redirect_url?: string;
  error_redirect_url?: string;
}

/**
 * Start OAuth2 authorization flow
 *
 * Calls the `startOAuth2Authorization` Cloud Function to generate
 * an OAuth2 authorization URL for the given email account.
 * @param accountId - Email account document ID
 * @param redirectUrls - Optional success/error redirect URLs
 * @param redirectUrls.success_redirect_url
 * @param redirectUrls.error_redirect_url
 * @returns Promise of OAuth2Authorization response with authorization_url
 * @throws {Error} If Cloud Function call fails
 * @example
 * const response = await startOAuth2Authorization("account123");
 * window.open(response.authorization_url, "_blank");
 */
export async function startOAuth2Authorization(
  accountId: string,
  redirectUrls?: {
    success_redirect_url?: string;
    error_redirect_url?: string;
  }
): Promise<OAuth2AuthorizationResponse> {
  try {
    const startOAuth2Function = httpsCallable<StartOAuth2AuthorizationRequest, OAuth2AuthorizationResponse>(
      functions,
      "oauth2FlowFunctions-startOAuth2Authorization"
    );

    const result = await startOAuth2Function({
      account_id: accountId,
      ...redirectUrls
    });

    return result.data;
  } catch (error) {
    console.error("Failed to start OAuth2 authorization:", error);

    if (error instanceof Error) {
      throw new TypeError(`OAuth2 authorization failed: ${error.message}`);
    }

    throw new Error("OAuth2 authorization failed: Unknown error");
  }
}

/**
 * OAuth2 callback handler (client-side)
 *
 * This function is NOT a Cloud Function call, but handles the OAuth2 callback
 * parameters on the client-side after the user completes authorization.
 *
 * The actual token exchange happens on the backend via the `oauth2Callback`
 * HTTP Cloud Function.
 * @param params - URL search params from OAuth2 provider redirect
 * @returns Parsed callback result
 */
export interface OAuth2CallbackParameters {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
  account_id?: string;
  email?: string;
}

/**
 * Parse OAuth2 callback URL parameters
 * @param searchParams - URLSearchParams from callback URL
 * @param searchParameters
 * @returns Parsed OAuth2 callback parameters
 * @example
 * // In callback page component:
 * const params = new URLSearchParams(window.location.search);
 * const result = parseOAuth2Callback(params);
 *
 * if (result.error) {
 *   console.error("OAuth2 error:", result.error);
 * } else {
 *   console.log("Account connected:", result.account_id);
 * }
 */
export function parseOAuth2Callback(searchParameters: URLSearchParams): OAuth2CallbackParameters {
  return {
    code: searchParameters.get("code") || undefined,
    state: searchParameters.get("state") || undefined,
    error: searchParameters.get("error") || undefined,
    error_description: searchParameters.get("error_description") || undefined,
    account_id: searchParameters.get("account_id") || undefined,
    email: searchParameters.get("email") || undefined
  };
}

/**
 * Check OAuth2 callback success/error
 * @param params - Parsed OAuth2 callback parameters
 * @param parameters
 * @returns Object with success boolean and error message if failed
 */
export function checkOAuth2CallbackResult(parameters: OAuth2CallbackParameters): {
  success: boolean;
  error?: string;
  accountId?: string;
  email?: string;
} {
  if (parameters.error) {
    return {
      success: false,
      error: parameters.error_description || parameters.error
    };
  }

  if (parameters.account_id) {
    return {
      success: true,
      accountId: parameters.account_id,
      email: parameters.email
    };
  }

  // No error but also no account_id - likely still processing
  return {
    success: false,
    error: "OAuth2 callback incomplete"
  };
}

/**
 * Request body for creating email account via Cloud Function
 */
export interface CreateEmailAccountCloudRequest {
  licenciada_id: string;
  email?: string; // Optional - will be populated from OAuth2 provider verification
  display_name: string;
  provider: "gmail" | "outlook" | "custom";
  oauth_config: {
    client_id: string;
    client_secret: string;
    scopes: string[];
    smtp_host?: string;
    smtp_port?: number;
  };
}

/**
 * Response from creating email account
 */
export interface CreateEmailAccountCloudResponse {
  account_id: string;
  email: string;
  provider: string;
  status: string;
}

/**
 * Create Email Account via Cloud Function
 *
 * Calls the `createEmailAccount` Cloud Function to create a new email account
 * with server-side encryption of the client_secret.
 * @param licenciadaId - Licenciada ID
 * @param data - Email account form data (includes client_secret in plain text)
 * @returns Promise of account ID
 * @throws {Error} If Cloud Function call fails
 * @example
 * const accountId = await createEmailAccountViaCloudFunction("lic123", {
 *   email: "contact@company.com",
 *   display_name: "Support",
 *   provider: "gmail",
 *   oauth_config: {
 *     client_id: "xxx",
 *     client_secret: "GOCSPX-xxx",
 *     redirect_uri: "http://127.0.0.1:5001/...",
 *     scopes: ["scope1"]
 *   }
 * });
 */
export async function createEmailAccountViaCloudFunction(
  licenciadaId: string,
  data: EmailAccountFormData
): Promise<string> {
  try {
    const createAccountFunction = httpsCallable<CreateEmailAccountCloudRequest, CreateEmailAccountCloudResponse>(
      functions,
      "emailAccountsFunctions-createEmailAccount"
    );

    const result = await createAccountFunction({
      licenciada_id: licenciadaId,
      email: data.email,
      display_name: data.display_name,
      provider: data.provider,
      oauth_config: {
        client_id: data.oauth_config.client_id,
        client_secret: data.oauth_config.client_secret,
        scopes: data.oauth_config.scopes,
        smtp_host: data.oauth_config.smtp_host,
        smtp_port: data.oauth_config.smtp_port
      }
    });

    return result.data.account_id;
  } catch (error) {
    console.error("Failed to create email account:", error);

    if (error instanceof Error) {
      throw new TypeError(`Email account creation failed: ${error.message}`);
    }

    throw new Error("Email account creation failed: Unknown error");
  }
}

/**
 * Get OAuth2 Redirect URI
 *
 * Fetches the fixed OAuth2 redirect URI from the backend.
 * This URL must be configured in the OAuth2 provider console.
 * @returns Promise of the redirect URI
 * @throws {Error} If Cloud Function call fails
 * @example
 * const redirectUri = await getOAuth2RedirectUri();
 * console.log("Configure this URL in your OAuth2 provider:", redirectUri);
 */
export async function getOAuth2RedirectUri(): Promise<string> {
  try {
    const getRedirectUriFunction = httpsCallable<void, { redirect_uri: string }>(
      functions,
      "emailAccountsFunctions-getOAuth2RedirectUri"
    );

    const result = await getRedirectUriFunction();
    return result.data.redirect_uri;
  } catch (error) {
    console.error("Failed to get OAuth2 redirect URI:", error);

    if (error instanceof Error) {
      throw new TypeError(`Failed to get redirect URI: ${error.message}`);
    }

    throw new Error("Failed to get redirect URI: Unknown error");
  }
}

/**
 * Helper to build OAuth2 redirect URLs for the current app
 * @param type - Type of redirect (success or error)
 * @returns Full redirect URL
 */
export function buildOAuth2RedirectUrl(type: "success" | "error"): string {
  const baseUrl = window.location.origin;
  return `${baseUrl}/oauth2/${type}`;
}

/**
 * Optional: Test email sending via Cloud Function
 * (If you have a testEmail Cloud Function for debugging)
 * @param accountId - Email account to use for sending
 * @param to - Recipient email
 * @returns Promise<void>
 */
export async function sendTestEmail(accountId: string, to: string): Promise<void> {
  try {
    const sendTestEmailFunction = httpsCallable<{ account_id: string; to: string }, { messageId: string }>(
      functions,
      "sendTestEmail"
    );

    const result = await sendTestEmailFunction({
      account_id: accountId,
      to
    });

    console.log("Test email sent:", result.data.messageId);
  } catch (error) {
    console.error("Failed to send test email:", error);
    throw error;
  }
}
